/**
 * @architecture-header 1
 * @layer module
 * @domain curriculum
 * @responsibility Render independent Desktop and Mobile KC directory compositions and state contracts.
 * @state-owner module query state and local KC editor drafts
 * @allowed-imports React; curriculum model; canonical Vispo loading state
 * @forbidden routes; app bootstrap; direct API clients
 * @public-api KnowledgeComponentDesktopSurface and KnowledgeComponentMobileSurface
 * @side-effects invokes caller-provided KC query and mutation callbacks
 * @tests KnowledgeComponentDirectorySurface.test.tsx
 */

import { useState } from 'react';
import { VispoLoading } from '../../../shared/components/WorkspaceState';
import type { CurriculumOrganizationKnowledgeComponent } from '../model/types';

export type KnowledgeComponentSurfaceStatus = 'loading' | 'empty' | 'error' | 'denied' | 'ready';

interface SurfaceProps {
  readonly status: KnowledgeComponentSurfaceStatus;
  readonly components?: readonly CurriculumOrganizationKnowledgeComponent[];
  readonly archivedComponents?: readonly CurriculumOrganizationKnowledgeComponent[];
  readonly showingArchived?: boolean;
  readonly search?: string;
  readonly onSearch?: (search: string) => void;
  readonly onRetry?: () => void;
  readonly onShowArchived?: (show: boolean) => void;
  readonly onCreate?: (input: { name: string; standardCode: string; description: string }) => void;
  readonly onUpdate?: (
    component: CurriculumOrganizationKnowledgeComponent,
    patch: {
      name?: string;
      standardCode?: string | null;
      description?: string;
      archived?: boolean;
    },
  ) => void;
  readonly mutationPending?: boolean;
  readonly mutationError?: unknown;
}

function State({ status, onRetry }: Pick<SurfaceProps, 'status' | 'onRetry'>) {
  if (status === 'loading') return <VispoLoading label="正在載入知識點…" />;
  if (status === 'empty') return <p role="status">目前沒有可用的知識點。</p>;
  if (status === 'denied') return <p role="alert">你目前沒有權限查看組織知識點。</p>;
  if (status === 'error')
    return (
      <div role="alert">
        <p>知識點暫時無法載入。</p>
        {onRetry ? (
          <button type="button" onClick={onRetry}>
            重試
          </button>
        ) : null}
      </div>
    );
  return null;
}

function ComponentEditor({
  component,
  mutationPending,
  onUpdate,
  onClose,
}: {
  readonly component: CurriculumOrganizationKnowledgeComponent;
  readonly mutationPending?: boolean;
  readonly onUpdate: NonNullable<SurfaceProps['onUpdate']>;
  readonly onClose: () => void;
}) {
  const [name, setName] = useState(component.name);
  const [standardCode, setStandardCode] = useState(component.standard_code ?? '');
  const [description, setDescription] = useState(component.description);
  return (
    <form
      aria-label={`編輯知識點 ${component.name}`}
      onSubmit={(event) => {
        event.preventDefault();
        onUpdate(component, {
          name,
          standardCode,
          description,
          // Reopen-with-rename is the supported remedy when an archived name
          // collides with another active KC.
          ...(component.archived ? { archived: false } : {}),
        });
        onClose();
      }}
    >
      <label>
        知識點名稱
        <input required value={name} onChange={(event) => setName(event.target.value)} />
      </label>
      <label>
        標準代碼
        <input value={standardCode} onChange={(event) => setStandardCode(event.target.value)} />
      </label>
      <label>
        知識點描述
        <textarea value={description} onChange={(event) => setDescription(event.target.value)} />
      </label>
      <button disabled={mutationPending} type="submit">
        {component.archived ? '重新開啟並儲存知識點' : '儲存知識點'}
      </button>
      <button type="button" onClick={onClose}>
        取消
      </button>
    </form>
  );
}

function ComponentCard({
  component,
  mutationPending,
  onUpdate,
  onEdit,
}: {
  readonly component: CurriculumOrganizationKnowledgeComponent;
  readonly mutationPending?: boolean;
  readonly onUpdate?: SurfaceProps['onUpdate'];
  readonly onEdit: () => void;
}) {
  return (
    <article>
      <h3>{component.name}</h3>
      {component.standard_code ? <p>{component.standard_code}</p> : null}
      <p>{component.description || '沒有描述'}</p>
      <button type="button" onClick={onEdit}>
        編輯 {component.name}
      </button>
      {onUpdate ? (
        <button
          disabled={mutationPending}
          type="button"
          onClick={() => onUpdate(component, { archived: !component.archived })}
        >
          {component.archived ? '重新開啟知識點' : '停用知識點'}
        </button>
      ) : null}
    </article>
  );
}

function CreateForm({
  mutationPending,
  onCreate,
  onClose,
}: {
  readonly mutationPending?: boolean;
  readonly onCreate: NonNullable<SurfaceProps['onCreate']>;
  readonly onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [standardCode, setStandardCode] = useState('');
  const [description, setDescription] = useState('');
  return (
    <form
      aria-label="建立知識點"
      onSubmit={(event) => {
        event.preventDefault();
        onCreate({ name, standardCode, description });
        onClose();
      }}
    >
      <label>
        知識點名稱
        <input required value={name} onChange={(event) => setName(event.target.value)} />
      </label>
      <label>
        標準代碼
        <input value={standardCode} onChange={(event) => setStandardCode(event.target.value)} />
      </label>
      <label>
        知識點描述
        <textarea value={description} onChange={(event) => setDescription(event.target.value)} />
      </label>
      <button disabled={mutationPending} type="submit">
        建立知識點
      </button>
    </form>
  );
}

function DesktopDirectory({
  components,
  mutationPending,
  onUpdate,
}: Pick<SurfaceProps, 'components' | 'mutationPending' | 'onUpdate'>) {
  const [editingId, setEditingId] = useState<string | null>(null);
  return (
    <ol aria-label="組織知識點清單" data-kc-layout="desktop-grid">
      {(components ?? []).map((component) => (
        <li key={component.id}>
          <ComponentCard
            component={component}
            mutationPending={mutationPending}
            onEdit={() => setEditingId(component.id)}
            onUpdate={onUpdate}
          />
          {editingId === component.id && onUpdate ? (
            <ComponentEditor
              component={component}
              mutationPending={mutationPending}
              onClose={() => setEditingId(null)}
              onUpdate={onUpdate}
            />
          ) : null}
        </li>
      ))}
    </ol>
  );
}

function MobileDirectory({
  components,
  mutationPending,
  onUpdate,
}: Pick<SurfaceProps, 'components' | 'mutationPending' | 'onUpdate'>) {
  const [editingId, setEditingId] = useState<string | null>(null);
  return (
    <ul aria-label="組織知識點清單" data-kc-layout="mobile-stack">
      {(components ?? []).map((component) => (
        <li key={component.id}>
          <ComponentCard
            component={component}
            mutationPending={mutationPending}
            onEdit={() => setEditingId(component.id)}
            onUpdate={onUpdate}
          />
          {editingId === component.id && onUpdate ? (
            <ComponentEditor
              component={component}
              mutationPending={mutationPending}
              onClose={() => setEditingId(null)}
              onUpdate={onUpdate}
            />
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function DesktopSurface({
  status,
  components,
  archivedComponents,
  showingArchived = false,
  search = '',
  onSearch,
  onRetry,
  onShowArchived,
  onCreate,
  onUpdate,
  mutationPending,
  mutationError,
}: SurfaceProps) {
  const [creating, setCreating] = useState(false);
  const visible = showingArchived ? archivedComponents : components;
  return (
    <section aria-label="組織知識點管理" data-knowledge-component-surface="desktop">
      <header>
        <h2>組織知識點</h2>
        <p>搜尋、建立與維護這個組織可選用的知識點</p>
      </header>
      {onSearch ? (
        <label>
          搜尋知識點
          <input value={search} onChange={(event) => onSearch(event.target.value)} />
        </label>
      ) : null}
      {onCreate ? (
        <>
          <button
            className="curriculum-action"
            type="button"
            onClick={() => setCreating((value) => !value)}
          >
            新增知識點
          </button>
          {creating ? (
            <CreateForm
              mutationPending={mutationPending}
              onClose={() => setCreating(false)}
              onCreate={onCreate}
            />
          ) : null}
        </>
      ) : null}
      {onShowArchived ? (
        <button
          className="curriculum-action"
          type="button"
          onClick={() => onShowArchived(!showingArchived)}
        >
          {showingArchived ? '返回使用中的知識點' : '查看已停用的知識點'}
        </button>
      ) : null}
      {mutationError ? <p role="alert">知識點操作失敗，請再試一次。</p> : null}
      {status === 'ready' ? (
        <DesktopDirectory
          components={visible}
          mutationPending={mutationPending}
          onUpdate={onUpdate}
        />
      ) : (
        <State onRetry={onRetry} status={status} />
      )}
    </section>
  );
}

function MobileSurface({
  status,
  components,
  archivedComponents,
  showingArchived = false,
  search = '',
  onSearch,
  onRetry,
  onShowArchived,
  onCreate,
  onUpdate,
  mutationPending,
  mutationError,
}: SurfaceProps) {
  const [creating, setCreating] = useState(false);
  const visible = showingArchived ? archivedComponents : components;
  return (
    <section aria-label="組織知識點管理" data-knowledge-component-surface="mobile">
      <div>
        <h2>組織知識點</h2>
        <p>快速搜尋與維護</p>
      </div>
      {onSearch ? (
        <label>
          搜尋知識點
          <input value={search} onChange={(event) => onSearch(event.target.value)} />
        </label>
      ) : null}
      {onCreate ? (
        <div>
          <button
            className="curriculum-action"
            type="button"
            onClick={() => setCreating((value) => !value)}
          >
            新增知識點
          </button>
          {creating ? (
            <CreateForm
              mutationPending={mutationPending}
              onClose={() => setCreating(false)}
              onCreate={onCreate}
            />
          ) : null}
        </div>
      ) : null}
      {onShowArchived ? (
        <button
          className="curriculum-action"
          type="button"
          onClick={() => onShowArchived(!showingArchived)}
        >
          {showingArchived ? '返回使用中的知識點' : '查看已停用的知識點'}
        </button>
      ) : null}
      {mutationError ? <p role="alert">知識點操作失敗，請再試一次。</p> : null}
      {status === 'ready' ? (
        <MobileDirectory
          components={visible}
          mutationPending={mutationPending}
          onUpdate={onUpdate}
        />
      ) : (
        <State onRetry={onRetry} status={status} />
      )}
    </section>
  );
}

export function KnowledgeComponentDesktopSurface(props: SurfaceProps) {
  return <DesktopSurface {...props} />;
}

export function KnowledgeComponentMobileSurface(props: SurfaceProps) {
  return <MobileSurface {...props} />;
}
